import { gofor } from '@fiverr-private/futile/lib/gofor';
import mapBodyToEntity from '../../utils/mapBodyToEntity';
import { toSnakeCase } from '../../utils/changeCase';
import * as Error from '../../utils/error';
import * as CollaboratorsErrors from '../../errors/collaborators';

/**
 * Create collaborators.
 * @param {object} payload
 * @param {array} payload.collaborators - The collaborators to create.
 * @param {number} payload.collaborators[].userId - The collaborator's user id.
 * @param {string} payload.collaborators[].email - The collaborator's email.
 * @param {string} payload.collaborators[].groupId - The group where to add the collaborator.
 * @param {string} payload.contextType
 * @param {(string|number)} payload.contextId
 * @returns {Promise}
 * @resolves {array} collaborators
 * @resolves {string} collaborators[].id - The collaborator id.
 * @resolves {number} collaborators[].userId - The collaborator's user id.
 * @resolves {string} collaborators[].groupId - The group where to add the collaborator.
 * @resolves {string} collaborators[].username - The collaborator's username.
 * @resolves {string} collaborators[].userImage - The collaborator's user image path.
 * @resolves {string} collaborators[].email - The collaborator's email.
 * @resolves {string} collaborators[].fullName - The collaborator's full name.
 * @resolves {number} collaborators[].lastSeen - The collaborator's last seen.
 * @resolves {boolean} collaborators[].removed - The collaborator was removed from its group.
 * @resolves {object} collaborators[].permissions - The collaborator permissions.
 * @resolves {boolean} collaborators[].permissions.invite - The collaborator can invite.
 * @rejects {Error}
 */
export const createCollaborators = (payload) =>
    Promise.resolve().then(() => {
        if (!payload) {
            throw Error.create(
                `Payload is falsy: ${payload}`,
                CollaboratorsErrors.COLLABORATORS_APIS_CREATE_COLLABORATORS_FALSY_PAYLOAD,
                payload
            );
        }
    }).then(() => {
        if (!payload.collaborators) {
            throw Error.create(
                `Collaborators is falsy: ${payload.collaborators}`,
                CollaboratorsErrors.COLLABORATORS_APIS_CREATE_COLLABORATORS_FALSY_COLLABORATORS,
                payload
            );
        }
    }).then(() => {
        const url = '/collaboration/collaborators/multi';
        const body = JSON.stringify(toSnakeCase(payload));
        const method = 'POST';

        return gofor(url, { method, body });
    }).then((response) => {
        if (response.status !== 201) {
            throw Error.create(
                `Http status received: ${response.status} expected: 201`,
                CollaboratorsErrors.COLLABORATORS_APIS_CREATE_COLLABORATORS_BAD_SERVER_STATUS,
                payload
            );
        }

        return response.json();
    }).then((body) =>
        Object.values(mapBodyToEntity(body, 'collaborators'))
    ).catch((error) => {
        if (!error.code) {
            error.code = CollaboratorsErrors.COLLABORATORS_APIS_CREATE_COLLABORATORS_UNEXPECTED;
            error.details = { payload: JSON.stringify(payload) };
        }

        throw error;
    });

/**
 * Update collaborator.
 * @param {object} payload
 * @param {string} payload.id
 * @param {string} [payload.username]
 * @param {string} [payload.fullName]
 * @param {number} [payload.lastSeen]
 * @param {boolean} [payload.emailNotificationEnabled]
 * @param {string} payload.contextType
 * @param {(string|number)} payload.contextId
 * @returns {Promise}
 * @rejects {Error}
 */
export const update = (payload) =>
    Promise.resolve().then(() => {
        if (!payload) {
            throw Error.create(
                `Payload is falsy: ${payload}`,
                CollaboratorsErrors.COLLABORATORS_APIS_UPDATE_FALSY_PAYLOAD,
                payload
            );
        }
    }).then(() => {
        if (!payload.id) {
            throw Error.create(
                `Id is falsy: ${payload.id}`,
                CollaboratorsErrors.COLLABORATORS_APIS_UPDATE_FALSY_ID,
                payload
            );
        }
    }).then(() => {
        const url = `/collaboration/collaborators/${payload.id}`;
        const body = JSON.stringify(toSnakeCase(payload));
        const method = 'PUT';

        return gofor(url, { method, body });
    }).then((response) => {
        if (response.status !== 202) {
            throw Error.create(
                `Http status received: ${response.status} expected: 202`,
                CollaboratorsErrors.COLLABORATORS_APIS_UPDATE_BAD_SERVER_STATUS,
                payload
            );
        }
    }).catch((error) => {
        if (!error.code) {
            error.code = CollaboratorsErrors.COLLABORATORS_APIS_UPDATE_UNEXPECTED;
            error.details = { payload: JSON.stringify(payload) };
        }

        throw error;
    });

/**
 * Remove collaborator
 * @param {object} payload
 * @param {string} payload.id - the removed collaborator id.
 * @param {string} payload.contextType
 * @param {(string|number)} payload.contextId
 * @returns {Promise}
 * @rejects {Error}
 */
export const remove = (payload) =>
    Promise.resolve().then(() => {
        if (!payload) {
            throw Error.create(
                `Payload is falsy: ${payload}`,
                CollaboratorsErrors.COLLABORATORS_APIS_REMOVE_FALSY_PAYLOAD,
                payload
            );
        }
    }).then(() => {
        if (!payload.id) {
            throw Error.create(
                `Id is falsy: ${payload.id}`,
                CollaboratorsErrors.COLLABORATORS_APIS_REMOVE_FALSY_ID,
                payload
            );
        }
    }).then(() => {
        const body = JSON.stringify(toSnakeCase(payload));
        const url = `/collaboration/collaborators/${payload.id}`;
        const method = 'DELETE';

        return gofor(url, { method, body });
    }).then((response) => {
        if (response.status !== 202) {
            throw Error.create(
                `Http status received: ${response.status} expected: 202`,
                CollaboratorsErrors.COLLABORATORS_APIS_REMOVE_BAD_SERVER_STATUS,
                payload
            );
        }
    }).catch((error) => {
        if (!error.code) {
            error.code = CollaboratorsErrors.COLLABORATORS_APIS_REMOVE_UNEXPECTED;
            error.details = { payload: JSON.stringify(payload) };
        }

        throw error;
    });

/**
 * Invite collaborators.
 * @param {object} payload
 * @param {string} payload.inviterCollaboratorId - The inviter's collaborator id.
 * @param {string} payload.inviterFullName - The inviter's full name to display in the email invitation.
 * @param {string} payload.personalMessage - The personal message to display in the email invitation.
 * @param {array<string>} payload.invitedCollaboratorIds - The invited collaborator ids.
 * @param {string} payload.contextType
 * @param {(string|number)} payload.contextId
 * @returns {Promise}
 * @rejects {Error}
 */
export const inviteCollaborators = (payload) =>
    Promise.resolve().then(() => {
        if (!payload) {
            throw Error.create(
                `Payload is falsy: ${payload}`,
                CollaboratorsErrors.COLLABORATORS_APIS_INVITE_COLLABORATORS_FALSY_PAYLOAD,
                payload
            );
        }
    }).then(() => {
        const url = '/collaboration/invitations';
        const body = JSON.stringify(toSnakeCase(payload));
        const method = 'POST';

        return gofor(url, { method, body });
    }).then((response) => {
        if (response.status !== 201) {
            throw Error.create(
                `Http status received: ${response.status} expected: 201`,
                CollaboratorsErrors.COLLABORATORS_APIS_INVITE_COLLABORATORS_BAD_SERVER_STATUS,
                payload
            );
        }
    }).catch((error) => {
        if (!error.code) {
            error.code = CollaboratorsErrors.COLLABORATORS_APIS_INVITE_COLLABORATORS_UNEXPECTED;
            error.details = { payload: JSON.stringify(payload) };
        }

        throw error;
    });
